import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AddExerciseDetails } from 'src/redux/actions'
import {
  buildSampleRows,
  importRows,
  lineOf,
  parseBoolean,
  parseNumber,
  parseStatus,
  requireValues,
  resolveOptionKey,
} from 'src/util/bulkImport'
import { ExcerciseTypes } from 'src/util/constants'
import { downloadCsv } from 'src/util/csv'

import BulkUploadPanel from '../helperComponents/BulkUploadPanel'

const MAX_OPTIONS = 5

// Which columns each question type actually uses — the same branching the
// single form's QuestionTypeForm applies when it renders a type's fields.

// Listening types play uploaded audio/audioSlow clips.
const HAS_AUDIO_TYPES = [
  ExcerciseTypes.LISTEN_WITH_IMAGE,
  ExcerciseTypes.LISTEN_WITH_AUDIO,
  ExcerciseTypes.LISTEN_WITH_TEXT,
  ExcerciseTypes.LISTEN_WITH_ORDER,
  ExcerciseTypes.LISTEN_WITH_TYPE,
  ExcerciseTypes.LISTEN_WITH_RECORD,
]
// Types that show the "prompt is an image or a video" toggle
// (isImage/exerciseImage/exerciseVideo).
const HAS_PROMPT_MEDIA_TYPES = [
  ExcerciseTypes.LISTEN_WITH_AUDIO,
  ExcerciseTypes.LISTEN_WITH_TEXT,
  ExcerciseTypes.LISTEN_WITH_ORDER,
  ExcerciseTypes.WATCH_WITH_AUDIO,
  ExcerciseTypes.WATCH_WITH_TEXT,
  ExcerciseTypes.WATCH_WITH_ORDER,
  ExcerciseTypes.WATCH_WITH_TYPE,
  ExcerciseTypes.WATCH_WITH_RECORD,
]
const HAS_VIDEO_URL_TYPES = [
  ExcerciseTypes.WATCH_WITH_AUDIO,
  ExcerciseTypes.WATCH_WITH_TEXT,
  ExcerciseTypes.WATCH_WITH_ORDER,
  ExcerciseTypes.WATCH_WITH_TYPE,
  ExcerciseTypes.WATCH_WITH_RECORD,
  ExcerciseTypes.WATCH_VIDEO,
]
const REQUIRED_VIDEO_URL_TYPES = [ExcerciseTypes.WATCH_WITH_AUDIO, ExcerciseTypes.WATCH_WITH_ORDER]
const REQUIRED_FILE_OPTION_TYPES = [
  ExcerciseTypes.LISTEN_WITH_IMAGE,
  ExcerciseTypes.LISTEN_WITH_AUDIO,
  ExcerciseTypes.WATCH_WITH_AUDIO,
]
// "Order" types support an optional pronunciation clip per option, alongside the text.
const OPTIONAL_FILE_OPTION_TYPES = [ExcerciseTypes.LISTEN_WITH_ORDER, ExcerciseTypes.WATCH_WITH_ORDER]
const TEXT_OPTION_TYPES = [
  ExcerciseTypes.LISTEN_WITH_TEXT,
  ExcerciseTypes.WATCH_WITH_TEXT,
  ExcerciseTypes.LISTEN_WITH_ORDER,
  ExcerciseTypes.WATCH_WITH_ORDER,
]
const NO_ANSWER_TYPES = [ExcerciseTypes.WATCH_VIDEO]

// Types whose answer names one of the options rather than being free text. The
// single form renders these as radio buttons over the options it has, so an
// answer that names nothing is a state the form cannot produce.
const OPTION_ANSWER_TYPES = [
  ExcerciseTypes.LISTEN_WITH_IMAGE,
  ExcerciseTypes.LISTEN_WITH_AUDIO,
  ExcerciseTypes.WATCH_WITH_AUDIO,
  ExcerciseTypes.LISTEN_WITH_TEXT,
  ExcerciseTypes.WATCH_WITH_TEXT,
]
// Types whose answer is the whole sentence the option words spell out.
const ORDER_ANSWER_TYPES = [ExcerciseTypes.LISTEN_WITH_ORDER, ExcerciseTypes.WATCH_WITH_ORDER]

const squash = value => String(value ?? '').toLowerCase().replace(/\s+/g, '')

// "Option 1", "option1" and "OPTION1" all name option1.
const canonicalOptionKey = value => {
  const match = squash(value).match(/^option([1-9][0-9]*)$/)
  return match ? `option${match[1]}` : null
}

// Which options this row actually fills — a file for media options, text otherwise.
const filledOptionKeys = (row, useFiles) => {
  const keys = []
  for (let n = 1; n <= MAX_OPTIONS; n++) {
    if (row[useFiles ? `option${n}Url` : `option${n}Text`]) keys.push(`option${n}`)
  }
  return keys
}

export const PRACTICE_ITEM_CSV_HEADERS = [
  'name', 'questionType', 'category', 'question', 'coin', 'status',
  'isImage', 'exerciseImageUrl', 'exerciseVideoUrl', 'videoUrl', 'audioUrl', 'audioSlowUrl', 'answer',
  'option1Url', 'option2Url', 'option3Url', 'option4Url', 'option5Url',
  'option1Text', 'option2Text', 'option3Text', 'option4Text', 'option5Text',
  'nativeOption1Text', 'nativeOption2Text', 'nativeOption3Text', 'nativeOption4Text', 'nativeOption5Text',
]

// One example per question type, showing exactly which columns that type uses.
const SAMPLE_PRACTICE_ITEMS = [
  {
    name: 'Choose the Correct Image - Apple',
    questionType: 'LISTEN WITH IMAGE',
    category: 'Listening',
    question: 'Listen to the audio and choose the correct image.',
    coin: '5',
    status: 'Active',
    audioUrl: 'https://example.com/media/apple_audio.mp3',
    audioSlowUrl: 'https://example.com/media/apple_audio_slow.mp3',
    answer: 'option1',
    option1Url: 'https://example.com/media/apple.jpg',
    option2Url: 'https://example.com/media/banana.jpg',
    option3Url: 'https://example.com/media/cherry.jpg',
  },
  {
    name: 'Choose the Matching Audio - Greeting',
    questionType: 'LISTEN WITH AUDIO',
    category: 'Listening',
    question: 'Listen to the prompt and choose the matching audio response.',
    coin: '5',
    status: 'Active',
    isImage: 'Yes',
    exerciseImageUrl: 'https://example.com/media/greeting_prompt.jpg',
    audioUrl: 'https://example.com/media/greeting_audio.mp3',
    audioSlowUrl: 'https://example.com/media/greeting_audio_slow.mp3',
    answer: 'option1',
    option1Url: 'https://example.com/media/greeting_option1.mp3',
    option2Url: 'https://example.com/media/greeting_option2.mp3',
  },
  {
    name: 'Choose the Correct Word - Family',
    questionType: 'LISTEN WITH TEXT',
    category: 'Reading',
    question: 'Listen and choose the correct word.',
    coin: '5',
    status: 'Active',
    isImage: 'No',
    exerciseVideoUrl: 'https://example.com/media/family_prompt.mp4',
    audioUrl: 'https://example.com/media/family_audio.mp3',
    audioSlowUrl: 'https://example.com/media/family_audio_slow.mp3',
    answer: 'option1',
    option1Text: 'Mother',
    nativeOption1Text: 'Mata',
    option2Text: 'Father',
    nativeOption2Text: 'Pita',
    option3Text: 'Sister',
    nativeOption3Text: 'Bahan',
  },
  {
    name: 'Order the Words - Good Morning',
    questionType: 'LISTEN WITH ORDER',
    category: 'Listening',
    question: 'Listen and arrange the words in the correct order.',
    coin: '10',
    status: 'Active',
    isImage: 'No',
    exerciseVideoUrl: '',
    audioUrl: 'https://example.com/media/good_morning_audio.mp3',
    audioSlowUrl: '',
    answer: 'Good Morning Everyone',
    option1Text: 'Good',
    nativeOption1Text: 'Accha',
    option1Url: 'https://example.com/media/good.mp3',
    option2Text: 'Morning',
    nativeOption2Text: 'Subah',
    option2Url: 'https://example.com/media/morning.mp3',
    option3Text: 'Everyone',
    nativeOption3Text: 'Sabhi',
    option3Url: 'https://example.com/media/everyone.mp3',
  },
  {
    name: 'Type What You Hear - Numbers',
    questionType: 'LISTEN WITH TYPE',
    category: 'Writing',
    question: 'Listen to the audio and type what you hear.',
    coin: '5',
    status: 'Active',
    audioUrl: 'https://example.com/media/numbers_audio.mp3',
    audioSlowUrl: 'https://example.com/media/numbers_audio_slow.mp3',
    answer: 'twenty five',
  },
  {
    name: 'Record What You Hear - Thank You',
    questionType: 'LISTEN WITH RECORD',
    category: 'Speaking',
    question: 'Listen to the audio and record yourself repeating it.',
    coin: '5',
    status: 'Active',
    audioUrl: 'https://example.com/media/thank_you_audio.mp3',
    audioSlowUrl: '',
    answer: 'Thank you',
  },
  {
    name: 'Watch and Choose Audio - Weather',
    questionType: 'WATCH WITH AUDIO',
    category: 'Listening',
    question: 'Watch the video and choose the matching audio response.',
    coin: '10',
    status: 'Active',
    isImage: 'No',
    exerciseVideoUrl: 'https://example.com/media/weather_prompt.mp4',
    videoUrl: 'https://example.com/media/weather_video.mp4',
    answer: 'option1',
    option1Url: 'https://example.com/media/weather_option1.mp3',
    option2Url: 'https://example.com/media/weather_option2.mp3',
  },
  {
    name: 'Watch and Choose the Word - Shopping',
    questionType: 'WATCH WITH TEXT',
    category: 'Reading',
    question: 'Watch the video and choose the correct word.',
    coin: '10',
    status: 'Active',
    isImage: 'Yes',
    exerciseImageUrl: 'https://example.com/media/shopping_prompt.jpg',
    videoUrl: 'https://example.com/media/shopping_video.mp4',
    answer: 'option1',
    option1Text: 'Store',
    nativeOption1Text: 'Dukan',
    option2Text: 'Market',
    nativeOption2Text: 'Bazaar',
  },
  {
    name: 'Watch and Order Words - At the Cafe',
    questionType: 'WATCH WITH ORDER',
    category: 'Listening',
    question: 'Watch the video and arrange the words in the correct order.',
    coin: '10',
    status: 'Inactive',
    isImage: 'No',
    exerciseVideoUrl: '',
    videoUrl: 'https://example.com/media/cafe_video.mp4',
    answer: 'Can I have a coffee',
    option1Text: 'Can',
    option2Text: 'I',
    option3Text: 'have',
    option4Text: 'a',
    option5Text: 'coffee',
  },
  {
    name: 'Watch and Type - Directions',
    questionType: 'WATCH WITH TYPE',
    category: 'Writing',
    question: 'Watch the video and type what is being said.',
    coin: '5',
    status: 'Active',
    isImage: 'No',
    exerciseVideoUrl: '',
    videoUrl: 'https://example.com/media/directions_video.mp4',
    answer: 'turn left',
  },
  {
    name: 'Watch and Record - Ordering Food',
    questionType: 'WATCH WITH RECORD',
    category: 'Speaking',
    question: 'Watch the video and record yourself repeating the phrase.',
    coin: '5',
    status: 'Active',
    isImage: 'No',
    exerciseVideoUrl: '',
    videoUrl: 'https://example.com/media/ordering_food_video.mp4',
    answer: 'I would like a coffee, please',
  },
  {
    name: 'Watch This Video - Culture Intro',
    questionType: 'WATCH VIDEO',
    category: 'Listening',
    question: 'Watch the introductory video about local culture.',
    coin: '0',
    status: 'Active',
    exerciseVideoUrl: 'https://example.com/media/culture_intro_upload.mp4',
    videoUrl: 'https://example.com/media/culture_intro.mp4',
  },
]

export const SAMPLE_PRACTICE_ITEM_CSV_ROWS = buildSampleRows(
  PRACTICE_ITEM_CSV_HEADERS,
  SAMPLE_PRACTICE_ITEMS
)

export const downloadSamplePracticeItemCsv = () =>
  downloadCsv('bulk-practice-items-sample.csv', SAMPLE_PRACTICE_ITEM_CSV_ROWS)

const buildOptionFields = (row, includeFile, includeText) => {
  const fields = {}
  for (let n = 1; n <= MAX_OPTIONS; n++) {
    if (includeFile && row[`option${n}Url`]) {
      fields[`option${n}File`] = row[`option${n}Url`]
    }
    if (includeText) {
      if (row[`option${n}Text`]) fields[`option${n}`] = row[`option${n}Text`]
      if (row[`nativeOption${n}Text`]) fields[`nativeOption${n}`] = row[`nativeOption${n}Text`]
    }
  }
  return fields
}

// Mirrors the payload AddEditExerciseDetails posts: the shared fields plus the
// child fields belonging to the chosen question type.
export const buildPracticeItemPayload = (row, { questionTypes, categories }) => {
  requireValues(row, ['name', 'question'])

  const questionType = resolveOptionKey(questionTypes, row.questionType, 'questionType')

  if (!NO_ANSWER_TYPES.includes(questionType) && !row.answer) {
    throw new Error('answer is required for this questionType')
  }
  if (REQUIRED_VIDEO_URL_TYPES.includes(questionType) && !row.videoUrl) {
    throw new Error('videoUrl is required for this questionType')
  }
  if (REQUIRED_FILE_OPTION_TYPES.includes(questionType) && !row.option1Url) {
    throw new Error('option1Url is required for this questionType')
  }
  if (TEXT_OPTION_TYPES.includes(questionType) && !row.option1Text) {
    throw new Error('option1Text is required for this questionType')
  }

  let answer = row.answer

  if (OPTION_ANSWER_TYPES.includes(questionType)) {
    const available = filledOptionKeys(row, REQUIRED_FILE_OPTION_TYPES.includes(questionType))
    const chosen = canonicalOptionKey(row.answer)
    if (!chosen || !available.includes(chosen)) {
      throw new Error(
        `answer must name one of the options this row fills (${available.join(', ') || 'none are filled'
        }) — got "${row.answer}"`
      )
    }
    // Stored the way the form spells it, so "Option 1" still matches option1.
    answer = chosen
  }

  const childFields = { answer }

  if (HAS_AUDIO_TYPES.includes(questionType)) {
    childFields.audio = row.audioUrl
    childFields.audioSlow = row.audioSlowUrl
  }
  if (HAS_PROMPT_MEDIA_TYPES.includes(questionType)) {
    const isImage = parseBoolean(row.isImage)
    childFields.isImage = isImage
    childFields.exerciseImage = isImage ? row.exerciseImageUrl : ''
    childFields.exerciseVideo = isImage ? '' : row.exerciseVideoUrl
  }
  if (HAS_VIDEO_URL_TYPES.includes(questionType)) {
    childFields.video = row.videoUrl
  }
  if (questionType === ExcerciseTypes.WATCH_VIDEO) {
    childFields.exerciseVideo = row.exerciseVideoUrl
  }

  const includeFileOptions =
    REQUIRED_FILE_OPTION_TYPES.includes(questionType) || OPTIONAL_FILE_OPTION_TYPES.includes(questionType)
  const includeTextOptions = TEXT_OPTION_TYPES.includes(questionType)
  if (includeFileOptions || includeTextOptions) {
    Object.assign(childFields, buildOptionFields(row, includeFileOptions, includeTextOptions))
  }

  return {
    name: row.name,
    questionType,
    category: resolveOptionKey(categories, row.category, 'category', { fallback: 'Reading' }),
    question: row.question,
    coin: parseNumber(row.coin, 'coin', { fallback: 0 }),
    isActive: parseStatus(row.status),
    ...childFields,
  }
}

// The columns a type reads, from the same lists the payload builder branches on.
const usedColumnsFor = questionType => {
  const used = new Set(['name', 'questionType', 'category', 'question', 'coin', 'status', 'answer'])

  if (HAS_AUDIO_TYPES.includes(questionType)) {
    used.add('audioUrl')
    used.add('audioSlowUrl')
  }
  if (HAS_PROMPT_MEDIA_TYPES.includes(questionType)) {
    used.add('isImage')
    used.add('exerciseImageUrl')
    used.add('exerciseVideoUrl')
  }
  if (HAS_VIDEO_URL_TYPES.includes(questionType)) used.add('videoUrl')
  if (questionType === ExcerciseTypes.WATCH_VIDEO) used.add('exerciseVideoUrl')

  const useFiles =
    REQUIRED_FILE_OPTION_TYPES.includes(questionType) ||
    OPTIONAL_FILE_OPTION_TYPES.includes(questionType)
  const useText = TEXT_OPTION_TYPES.includes(questionType)
  for (let n = 1; n <= MAX_OPTIONS; n++) {
    if (useFiles) used.add(`option${n}Url`)
    if (useText) {
      used.add(`option${n}Text`)
      used.add(`nativeOption${n}Text`)
    }
  }

  return used
}

/**
 * Content a row supplies that its question type will quietly drop, plus an
 * "order" answer the options cannot spell — neither stops the import, but both
 * mean the stored item is not what the author wrote.
 */
export const findItemWarnings = (row, questionType) => {
  const warnings = []

  const used = usedColumnsFor(questionType)
  const ignored = PRACTICE_ITEM_CSV_HEADERS.filter(
    header => header !== 'name' && row[header] && !used.has(header)
  )
  if (ignored.length) {
    warnings.push(
      `${questionType} does not use ${ignored.join(', ')} — ${ignored.length > 1 ? 'those values were' : 'that value was'
      } not saved`
    )
  }

  if (ORDER_ANSWER_TYPES.includes(questionType)) {
    const options = []
    for (let n = 1; n <= MAX_OPTIONS; n++) {
      if (row[`option${n}Text`]) options.push(squash(row[`option${n}Text`]))
    }
    const answer = squash(row.answer)
    const spelledOut =
      options.length > 0 &&
      options.every(option => answer.includes(option)) &&
      answer.length === options.reduce((total, option) => total + option.length, 0)
    if (!spelledOut) {
      warnings.push(
        `answer "${row.answer}" is not the option words rearranged, so the exercise may have no correct solution`
      )
    }
  }

  return warnings
}

const BulkUploadPracticeItem = ({ toggleDrawer, refetch }) => {
  const dispatch = useDispatch()
  const { exerciseOptionTypes, exerciseCategories } = useSelector(store => store.mainReducer)

  const onImport = useCallback(
    async rows => {
      const warnings = []

      const result = await importRows(rows, async (row, index) => {
        const payload = buildPracticeItemPayload(row, {
          questionTypes: exerciseOptionTypes,
          categories: exerciseCategories,
        })
        // Imported, but not as written — content the type drops, or an "order"
        // answer its options cannot spell.
        findItemWarnings(row, payload.questionType).forEach(reason =>
          warnings.push({ line: lineOf(row, index), name: row.name || '(blank)', reason })
        )
        await dispatch(AddExerciseDetails(payload))
      })

      return { ...result, warnings }
    },
    [dispatch, exerciseOptionTypes, exerciseCategories]
  )

  return (
    <BulkUploadPanel
      entityPlural="Practice Items"
      headers={PRACTICE_ITEM_CSV_HEADERS}
      onDownloadSample={downloadSamplePracticeItemCsv}
      onImport={onImport}
      toggleDrawer={toggleDrawer}
      refetch={refetch}
      helpText={
        `questionType accepts the code or the label, e.g. ${(exerciseOptionTypes || [])
          .slice(0, 2)
          .map(type => `"${type.key}"`)
          .join(' / ')}. The sample CSV has one row per type showing exactly which columns it uses — ` +
        'columns that do not apply to a type can stay blank. answer is required for every type except ' +
        'WATCH VIDEO: for option-based types it is the correct option key (option1, option2, ...), for ' +
        '"order" types the full correct sentence, and for "type"/"record" types the expected text. ' +
        'All media columns take direct URLs.'
      }
    />
  )
}

export default BulkUploadPracticeItem
